import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listAuditYears,
  createAuditYear,
  updateAuditYear,
  lockAuditYear,
  deleteAuditYear,
  getLockReadiness,
  previewBackfill,
  backfillAuditYear,
  type AuditYear,
  type CreateAuditYearPayload,
  type UpdateAuditYearPayload,
} from '../api/auditYears.ts';
import { useAuditYear } from '../contexts/AuditYearContext.tsx';

const QUERY_KEY = ['audit-years'] as const;

export function useAuditYears() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: listAuditYears,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateAuditYear() {
  const qc = useQueryClient();
  const { setSelectedAuditYearId } = useAuditYear();
  return useMutation({
    mutationFn: (payload: CreateAuditYearPayload) => createAuditYear(payload),
    onSuccess: (newYear: AuditYear) => {
      // Immediately switch context to the new year — don't wait for the async refetch cycle.
      if (newYear?.id) setSelectedAuditYearId(newYear.id);
      qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

export function useUpdateAuditYear() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateAuditYearPayload }) =>
      updateAuditYear(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useLockAuditYear() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => lockAuditYear(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useDeleteAuditYear() {
  const qc = useQueryClient();
  const { selectedAuditYearId, setAuditYears, auditYears } = useAuditYear();
  return useMutation({
    mutationFn: (id: string) => deleteAuditYear(id),
    onSuccess: (_data, deletedId) => {
      // Drop the deleted year from context immediately so the stale UUID is cleared before
      // the query refetch completes. setAuditYears auto-selects the next active year.
      const remaining = auditYears.filter((y) => y.id !== deletedId);
      if (deletedId === selectedAuditYearId) {
        setAuditYears(remaining);
      }
      qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

/**
 * Fetch lock readiness for an audit year.
 * Returns blockers (hard — must resolve before locking) and warnings (soft — informational).
 * Disabled until an id is provided; always fetches fresh when opened.
 */
export function useLockReadiness(id: string | null) {
  return useQuery({
    queryKey: ['audit-year-lock-readiness', id],
    queryFn: () => getLockReadiness(id!),
    enabled: !!id,
    staleTime: 0,
  });
}

/** Fetch unscoped record counts (read-only preview). Disabled by default — enable by passing the id. */
export function useBackfillPreview(id: string | null) {
  return useQuery({
    queryKey: ['audit-year-backfill-preview', id],
    queryFn: () => previewBackfill(id!),
    enabled: !!id,
    staleTime: 0, // always fresh when opened
  });
}

/** Execute the backfill — assigns all NULL audit_year_id rows to the given year. */
export function useBackfillAuditYear() {
  return useMutation({
    mutationFn: (id: string) => backfillAuditYear(id),
  });
}
